import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Flame, Snowflake, ArrowLeft, TrendingUp, TrendingDown, Thermometer } from 'lucide-react'
import { config } from '@workspace/public-utils'
import { Seo } from '../../components/Seo'

interface NumberAnalysis {
  number: number
  frequency: number
  percentage: number
}

interface AnalysisData {
  round_range: number
  hot_numbers: NumberAnalysis[]
  cold_numbers: NumberAnalysis[]
  total_rounds_analyzed: number
  calculated_at: string
}

const HotColdAnalysisPage = () => {
  const [selectedRange, setSelectedRange] = useState(0) // 0 = 10, 1 = 30, 2 = 50, 3 = custom
  const [analysisData, setAnalysisData] = useState<Record<number, AnalysisData>>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [customStartRound, setCustomStartRound] = useState<string>('')
  const [customEndRound, setCustomEndRound] = useState<string>('')

  const ranges = [10, 30, 50]

  useEffect(() => {
    if (selectedRange < 3) {
      fetchAnalysis(ranges[selectedRange])
    }
  }, [selectedRange])

  const handleCustomRangeSubmit = () => {
    const start = parseInt(customStartRound)
    const end = parseInt(customEndRound)
    
    if (isNaN(start) || isNaN(end)) {
      alert('올바른 회차 번호를 입력해주세요')
      return
    }
    
    if (start >= end) {
      alert('시작 회차는 종료 회차보다 작아야 합니다')
      return
    }
    
    if (end - start > 100) {
      alert('최대 100회차까지만 분석 가능합니다')
      return
    }
    
    fetchCustomRangeAnalysis(start, end)
  }

  const fetchAnalysis = async (range: number) => {
    try {
      setLoading(true)
      setError(null)

      const response = await fetch(
        `${config.API_BASE_URL}/api/v1/lottery/analysis?range=${range}`
      )

      if (!response.ok) {
        throw new Error('분석 데이터를 불러오는데 실패했습니다.')
      }

      const data = await response.json()
      setAnalysisData((prev) => ({ ...prev, [range]: data }))
    } catch (err) {
      console.error('Error fetching analysis:', err)
      setError(err instanceof Error ? err.message : '데이터를 불러올 수 없습니다.')
    } finally {
      setLoading(false)
    }
  }

  const fetchCustomRangeAnalysis = async (startRound: number, endRound: number) => {
    try {
      setLoading(true)
      setError(null)

      // Fetch results for custom range using start_round and end_round parameters
      const range = endRound - startRound + 1
      const response = await fetch(
        `${config.API_BASE_URL}/api/v1/public/lottery/results?start_round=${startRound}&end_round=${endRound}`
      )

      if (!response.ok) {
        throw new Error('분석 데이터를 불러오는데 실패했습니다.')
      }

      const data = await response.json()
      const results = data.items || data.results || []
      
      if (results.length === 0) {
        throw new Error('해당 범위의 데이터가 없습니다.')
      }
      
      // Calculate hot/cold numbers from the results
      const numberFrequency: Record<number, number> = {}
      let totalNumbers = 0
      
      results.forEach((result: any) => {
        if (result && result.numbers && Array.isArray(result.numbers)) {
          result.numbers.forEach((num: number) => {
            numberFrequency[num] = (numberFrequency[num] || 0) + 1
            totalNumbers++
          })
        }
      })
      
      const frequencyArray = Object.entries(numberFrequency)
        .map(([num, freq]) => ({
          number: Number(num),
          frequency: freq,
          percentage: (freq / totalNumbers) * 100
        }))
        .sort((a, b) => b.frequency - a.frequency)
      
      const hot_numbers = frequencyArray.slice(0, 10)
      const cold_numbers = frequencyArray.slice(-10).reverse()
      
      const customAnalysisData = {
        round_range: range,
        hot_numbers,
        cold_numbers,
        total_rounds_analyzed: results.length,
        calculated_at: new Date().toISOString()
      }
      
      setAnalysisData((prev) => ({ ...prev, [`custom_${startRound}_${endRound}`]: customAnalysisData }))
    } catch (err) {
      console.error('Error fetching custom range analysis:', err)
      setError(err instanceof Error ? err.message : '데이터를 불러올 수 없습니다.')
    } finally {
      setLoading(false)
    }
  }

  const getCurrentData = (): AnalysisData | undefined => {
    if (selectedRange === 3 && customStartRound && customEndRound) {
      const key = `custom_${customStartRound}_${customEndRound}`
      return analysisData[key as unknown as number] as AnalysisData | undefined
    }
    return analysisData[ranges[selectedRange]]
  }

  const currentData = getCurrentData()

  const renderNumberBall = (num: number, type: 'hot' | 'cold', frequency: number, percentage: number) => {
    const isHot = type === 'hot'
    
    return (
      <motion.div
        key={num}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        whileHover={{ scale: 1.1 }}
        className={`relative rounded-2xl shadow-lg p-6 text-center transition-all duration-300 hover:shadow-2xl ${
          isHot 
            ? 'bg-gradient-to-br from-red-500 to-orange-500' 
            : 'bg-gradient-to-br from-blue-500 to-cyan-500'
        } text-white`}
      >
        {/* Icon */}
        <div className="flex justify-center mb-2">
          {isHot ? (
            <Flame className="w-8 h-8 animate-pulse" />
          ) : (
            <Snowflake className="w-8 h-8 animate-spin-slow" />
          )}
        </div>

        {/* Number */}
        <div className="text-5xl font-black mb-2">{num}</div>

        {/* Stats */}
        <div className="text-sm opacity-90 mb-1">
          {frequency}회 출현
        </div>
        <div className="inline-block px-3 py-1 bg-white/30 rounded-full text-xs font-bold backdrop-blur-sm">
          {percentage.toFixed(1)}%
        </div>

        {/* Decorative elements */}
        <div className="absolute top-2 right-2 w-8 h-8 bg-white/10 rounded-full"></div>
        <div className="absolute bottom-2 left-2 w-6 h-6 bg-white/10 rounded-full"></div>
      </motion.div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors">
      <Seo
        title="핫 & 콜드 넘버 분석"
        description="최근 출현 빈도 기반으로 핫(자주 나온) 번호와 콜드(덜 나온) 번호를 비교 분석해 보세요."
        canonicalPath="/analysis/hot-cold"
      />
      {/* Header */}
      <section className="bg-gradient-to-br from-red-500 via-orange-500 to-blue-500 py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/analysis" className="inline-flex items-center text-white hover:text-white/80 mb-6 transition-colors">
            <ArrowLeft className="w-5 h-5 mr-2" />
            분석 방법으로 돌아가기
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center justify-center mb-4">
              <Thermometer className="w-12 h-12 text-white mr-4" />
              <h1 className="text-4xl md:text-6xl font-black text-white">
                핫 & 콜드 넘버
              </h1>
            </div>
            <p className="text-xl text-white/90 text-center max-w-3xl mx-auto">
              특정 기간 동안 자주 나온 숫자(Hot)와 적게 나온 숫자(Cold)를 한눈에 확인하세요
            </p>
          </motion.div>
        </div>
      </section>

      {/* Range Selector */}
      <section className="py-8 px-4 bg-white shadow-sm dark:bg-gray-900 dark:shadow-none dark:border-b dark:border-gray-800 transition-colors">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">분석 기간 선택</h2>
              <div className="flex flex-wrap gap-2">
                {ranges.map((range, index) => (
                  <button
                    key={range}
                    onClick={() => setSelectedRange(index)}
                    className={`px-6 py-3 rounded-lg font-bold transition-all duration-300 ${
                      selectedRange === index
                        ? 'bg-gradient-to-r from-happiness-primary to-purple-600 text-white shadow-lg scale-105'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    최근 {range}회
                  </button>
                ))}
                <button
                  onClick={() => setSelectedRange(3)}
                  className={`px-6 py-3 rounded-lg font-bold transition-all duration-300 ${
                    selectedRange === 3
                      ? 'bg-gradient-to-r from-happiness-primary to-purple-600 text-white shadow-lg scale-105'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  사용자 지정
                </button>
              </div>
            </div>
            
            {/* Custom Range Inputs */}
            {selectedRange === 3 && (
              <div className="flex flex-col md:flex-row items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg transition-colors">
                <div className="flex items-center gap-2">
                  <label className="font-medium text-gray-700 dark:text-gray-300">시작 회차:</label>
                  <input
                    type="number"
                    value={customStartRound}
                    onChange={(e) => setCustomStartRound(e.target.value)}
                    placeholder="예: 1000"
                    className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-happiness-primary"
                    min="1"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="font-medium text-gray-700 dark:text-gray-300">종료 회차:</label>
                  <input
                    type="number"
                    value={customEndRound}
                    onChange={(e) => setCustomEndRound(e.target.value)}
                    placeholder="예: 1100"
                    className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-happiness-primary"
                    min="1"
                  />
                </div>
                <button
                  onClick={handleCustomRangeSubmit}
                  className="px-6 py-2 bg-gradient-to-r from-happiness-primary to-purple-600 text-white font-bold rounded-lg hover:shadow-lg transition-all duration-300"
                >
                  분석하기
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-happiness-primary"></div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="bg-red-50 dark:bg-red-950/40 border-l-4 border-red-500 p-6 rounded-lg">
            <p className="text-red-700 dark:text-red-300 font-medium">{error}</p>
          </div>
        </div>
      )}

      {/* Analysis Content */}
      {!loading && !error && currentData && (
        <div className="max-w-7xl mx-auto px-4 py-12">
          {/* Info Box */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-blue-50 dark:bg-blue-950/40 border-l-4 border-blue-500 p-6 rounded-lg mb-12"
          >
            <p className="text-gray-700 dark:text-gray-300">
              <strong>분석 기준:</strong> 최근 {currentData.round_range}회차 ({currentData.total_rounds_analyzed}회 분석됨)
              <br />
              <strong>업데이트:</strong> {new Date(currentData.calculated_at).toLocaleString('ko-KR')}
            </p>
          </motion.div>

          {/* Hot Numbers Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-16"
          >
            <div className="flex items-center mb-8">
              <TrendingUp className="w-8 h-8 text-red-500 mr-3" />
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                뜨거운 번호 (Hot Numbers)
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
              {currentData.hot_numbers.map((item: NumberAnalysis) => 
                renderNumberBall(item.number, 'hot', item.frequency, item.percentage)
              )}
            </div>
          </motion.div>

          {/* Cold Numbers Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="flex items-center mb-8">
              <TrendingDown className="w-8 h-8 text-blue-500 mr-3" />
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                차가운 번호 (Cold Numbers)
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
              {currentData.cold_numbers.map((item: NumberAnalysis) => 
                renderNumberBall(item.number, 'cold', item.frequency, item.percentage)
              )}
            </div>
          </motion.div>

          {/* Disclaimer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-12 bg-yellow-50 dark:bg-yellow-950/30 border-l-4 border-yellow-500 p-6 rounded-lg"
          >
            <p className="text-gray-700 dark:text-gray-300">
              <strong className="text-yellow-700 dark:text-yellow-400">⚠️ 안내:</strong> 이 분석은 과거 당첨 번호의 통계적 분석이며, 
              미래 당첨 번호를 예측하거나 보장하지 않습니다. 
              로또는 무작위 추첨이므로 모든 번호는 동일한 당첨 확률을 가집니다.
            </p>
          </motion.div>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && !currentData && (
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
          <p className="text-xl text-gray-600 dark:text-gray-400">
            분석 데이터가 없습니다. 로또 결과 데이터가 충분히 쌓이면 분석이 표시됩니다.
          </p>
        </div>
      )}
    </div>
  )
}

export default HotColdAnalysisPage

